// Capability gating from UX spec section 2. `allowed` (this principal's grant) and
// `status` (implementation/configuration/health) are separate dimensions.
import type { Capability, Config } from '@bcio-voice/contracts';

export type CapabilityName =
  | 'calling.internal'
  | 'calling.pstn.inbound'
  | 'calling.pstn.outbound'
  | 'calling.transfer'
  | 'calling.conference'
  | 'supervision.listen'
  | 'supervision.whisper'
  | 'supervision.barge'
  | 'recording.capture'
  | 'recording.playback'
  | 'transcription.live'
  | 'coaching.live'
  | 'messaging.sms'
  | 'messaging.mms'
  | 'crm.sync'
  | 'analytics.read'
  | 'desktop.capture';

export type Gate =
  | { render: 'normal'; detail: string | null }
  | { render: 'degraded'; detail: string | null }
  | { render: 'hidden'; reason: 'not_allowed' | 'unknown'; detail: string | null }
  | { render: 'explain'; reason: 'disabled' | 'unconfigured' | 'unimplemented'; detail: string | null };

export function capability(config: Config | null, name: CapabilityName): Capability | null {
  return config?.capabilities[name] ?? null;
}

/** A missing capability (config not loaded or name absent) is treated as unavailable, never as on. */
export function gate(cap: Capability | null | undefined): Gate {
  if (!cap) return { render: 'hidden', reason: 'unknown', detail: null };
  if (!cap.allowed) return { render: 'hidden', reason: 'not_allowed', detail: cap.detail };
  switch (cap.status) {
    case 'enabled':
      return { render: 'normal', detail: cap.detail };
    case 'degraded':
      return { render: 'degraded', detail: cap.detail };
    case 'disabled':
      return { render: 'explain', reason: 'disabled', detail: cap.detail };
    case 'unconfigured':
      return { render: 'explain', reason: 'unconfigured', detail: cap.detail };
    default:
      return { render: 'explain', reason: 'unimplemented', detail: cap.detail };
  }
}

export function usable(cap: Capability | null | undefined): boolean {
  const g = gate(cap);
  return g.render === 'normal' || g.render === 'degraded';
}

export function gateHeadline(g: Gate, featureLabel: string): string {
  if (g.render === 'normal') return `${featureLabel} is available`;
  if (g.render === 'degraded') return `${featureLabel} is degraded`;
  if (g.render === 'hidden') return g.reason === 'not_allowed' ? `Your role doesn't include ${featureLabel.toLowerCase()}` : `${featureLabel} status is unknown`;
  if (g.reason === 'disabled') return `${featureLabel} is turned off`;
  if (g.reason === 'unconfigured') return `${featureLabel} isn't set up yet`;
  return `${featureLabel} isn't available in this version`;
}

export function hasPermission(permissions: readonly string[] | null | undefined, permission: string): boolean {
  return !!permissions?.includes(permission);
}
